/**
 * StickyATC
 *
 * Fixed bottom bar that appears when the main ATC button scrolls above
 * the viewport. Variant options are displayed inline in the bar.
 * Adds to cart via the Shopify Cart API and opens the cart drawer.
 */

class StickyATC {
  constructor(sectionId) {
    this.sectionId    = sectionId;
    this.bar          = document.getElementById(`StickyATC-${sectionId}`);
    this.mainBtn      = document.getElementById(`ProductSubmitButton-${sectionId}`);
    this.stickyBtn    = document.getElementById(`StickyATCBtn-${sectionId}`);
    this.priceEl      = document.getElementById(`StickyATCPrice-${sectionId}`);
    this.mainForm     = document.getElementById(`product-form-${sectionId}`);
    this.mainInput    = this.mainForm?.querySelector('input[name="id"]');
    this.defaultButtonText = this.stickyBtn?.textContent.trim() || 'Add to cart';

    const dataEl      = document.getElementById(`StickyATCVariantsData-${sectionId}`);
    this.variants     = dataEl ? JSON.parse(dataEl.textContent) : [];
    this.hasVariants  = this.variants.length > 1;

    // Currently selected options, 0-indexed [option1, option2, option3]
    this.selectedOptions = this._optionsFromId(parseInt(this.mainInput?.value, 10));

    if (!this.bar || !this.mainBtn) return;

    // Suppress transition until the first IntersectionObserver callback fires,
    // preventing the bar from flashing into view on page load / refresh.
    this.bar.classList.add('sticky-atc--no-transition');

    this._init();
  }

  /* ── Setup ──────────────────────────────────────────────── */

  _init() {
    this._observeMainButton();
    this._syncBar();
    this._syncSwatches();

    // Swatch clicks in the bar
    this.bar.querySelectorAll('.sticky-atc__swatch').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-unavailable')) return;
        const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
        this.selectedOptions[pos] = btn.dataset.optionValue;
        this._syncSwatches();
        this._pushToMainPicker();
      });
    });

    // Mobile select dropdowns
    this.bar.querySelectorAll('.sticky-atc__select').forEach(sel => {
      sel.addEventListener('change', () => {
        const pos = parseInt(sel.dataset.optionPosition, 10) - 1;
        this.selectedOptions[pos] = sel.value;
        this._syncSwatches();
        this._pushToMainPicker();
      });
    });

    // Flag button, smooth scroll up to product title
    const flagBtn = document.getElementById(`StickyFlagBtn-${this.sectionId}`);
    if (flagBtn) {
      flagBtn.addEventListener('click', () => {
        const target =
          document.querySelector(`#MainProduct-${this.sectionId} .product__title__wrapper`) ||
          document.querySelector(`#MainProduct-${this.sectionId} .product__title`) ||
          document.getElementById(`MainProduct-${this.sectionId}`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    // ATC button
    this.stickyBtn?.addEventListener('click', () => this._handleAdd());

    // Keep bar in sync when user picks a variant on the main product section
    this._watchMainPicker();
  }

  /* ── IntersectionObserver ───────────────────────────────── */

  _observeMainButton() {
    let firstCallback = true;
    const io = new IntersectionObserver(([entry]) => {
      // On the very first callback, remove the no-transition guard so that
      // subsequent show/hide animate normally. Use rAF to ensure the browser
      // has already committed the initial (hidden) paint before re-enabling.
      if (firstCallback) {
        firstCallback = false;
        requestAnimationFrame(() => {
          this.bar.classList.remove('sticky-atc--no-transition');
        });
      }
      // Show only when the button has scrolled ABOVE the viewport
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.bar.classList.toggle('sticky-atc--visible', show);
      this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { threshold: 0 });
    io.observe(this.mainBtn);
  }

  /* ── Handle ATC click ───────────────────────────────────── */

  _handleAdd() {
    const variant = this._currentVariant();

    // If product has variants and nothing is fully resolved, shake unselected groups
    if (this.hasVariants && !variant) {
      this._shakeUnselected();
      return;
    }

    const id = variant ? variant.id : parseInt(this.mainInput?.value, 10);
    if (!id) return;

    this._addToCart(id);
  }

  /* ── Add to cart ────────────────────────────────────────── */

  _addToCart(variantId) {
    if (!variantId || isNaN(variantId)) return;

    const stickyBtn = this.stickyBtn;
    const original  = stickyBtn?.textContent;
    const restore   = () => {
      if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = original; }
    };

    if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = 'Adding…'; }

    // ── Primary: delegate to the main form's submit button ───────────────
    //
    // The theme's product-form.js handles the POST, cart drawer HTML refresh,
    // and drawer open. We get all of that for free by clicking its button
    // after syncing the hidden variant input to our chosen variant.

    if (this.mainInput) {
      this.mainInput.value = variantId;
      this.mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const mainSubmitBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (mainSubmitBtn) {
      // Un-disable temporarily so the click always registers
      const wasDisabled = mainSubmitBtn.disabled;
      mainSubmitBtn.disabled = false;

      requestAnimationFrame(() => {
        mainSubmitBtn.click();
        requestAnimationFrame(() => { mainSubmitBtn.disabled = wasDisabled; });
        // Restore sticky button text after the theme's loading indicator clears
        setTimeout(restore, 2200);
      });

      return; // theme JS takes it from here
    }

    // ── Fallback: direct fetch (main button not found) ───────────────────
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(async res => {
        const body = await res.json();
        if (!res.ok) throw new Error(body.description || body.message || `HTTP ${res.status}`);
        return body;
      })
      .then(() => {
        if (stickyBtn) stickyBtn.textContent = '✓ Added';
        setTimeout(restore, 1800);
        // Ask the cart drawer to refresh its contents then open
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        const cartDrawer = document.querySelector('cart-drawer');
        if (typeof cartDrawer?.open === 'function') cartDrawer.open();
        else document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      })
      .catch(err => {
        console.error('[StickyATC] Add to cart failed:', err.message);
        restore();
      });
  }

  /* ── Sync bar price from main price element ─────────────── */

  _syncBar() {
    if (!this.priceEl) return;
    // Read from the main price element so formatting (currency, sale) is already correct
    const src = document.querySelector(
      `#price-${this.sectionId} .price-item--sale, ` +
      `#price-${this.sectionId} .price-item--regular`
    );
    if (src) this.priceEl.textContent = src.textContent.trim();
  }

  /* ── Sync swatch active / unavailable states ─────────────── */

  _syncSwatches() {
    if (!this.hasVariants) return;

    this.bar.querySelectorAll('.sticky-atc__swatch').forEach(btn => {
      const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
      const val = btn.dataset.optionValue;

      // Active
      btn.classList.toggle('is-active', this.selectedOptions[pos] === val);
      btn.setAttribute('aria-pressed', this.selectedOptions[pos] === val ? 'true' : 'false');

      // Availability: test this value against all other currently selected options
      const test = [...this.selectedOptions];
      test[pos] = val;
      const available = this.variants.some(v => {
        const opts = [v.option1, v.option2, v.option3];
        return test.every((o, i) => o == null || opts[i] === o) && v.available;
      });
      btn.classList.toggle('is-unavailable', !available);
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', available ? 'false' : 'true');
    });

    // ATC button: disable only if the fully-resolved variant is out of stock
    const variant = this._currentVariant();
    if (this.stickyBtn) {
      const canAdd = Boolean(variant?.available);
      this.stickyBtn.disabled = !canAdd;
      this.stickyBtn.textContent = canAdd ? this.defaultButtonText : 'Sold out';
    }

    // Sync mobile selects, set selected value and disable unavailable options
    this.bar.querySelectorAll('.sticky-atc__select').forEach(sel => {
      const pos = parseInt(sel.dataset.optionPosition, 10) - 1;
      if (this.selectedOptions[pos] != null) sel.value = this.selectedOptions[pos];

      Array.from(sel.options).forEach(opt => {
        const test = [...this.selectedOptions];
        test[pos] = opt.value;
        const available = this.variants.some(v => {
          const opts = [v.option1, v.option2, v.option3];
          return test.every((o, i) => o == null || opts[i] === o) && v.available;
        });
        opt.disabled = !available;
        // Strip any prior suffix then re-apply so label stays clean on re-sync
        opt.text = opt.value + (!available ? ', SOLD OUT' : '');
      });
    });
  }

  /* ── Push sticky selection back to main variant picker ───── */

  _pushToMainPicker() {
    const variant = this._currentVariant();
    if (!variant || !this.mainInput) return;

    // Update hidden input
    this.mainInput.value = variant.id;

    // Try to click the matching radio/button in the main picker so its UI updates
    const section = document.getElementById(`MainProduct-${this.sectionId}`);
    this.selectedOptions.forEach((val, i) => {
      if (!val) return;
      const pos = i + 1;
      // Radio input pattern (Dawn buttons)
      const radio = section?.querySelector(
        `input[type="radio"][data-option-value="${val}"][name*="option${pos}"],` +
        `input[type="radio"][value="${val}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // Select element pattern (Dawn dropdown)
      const select = section?.querySelector(`select[id*="Option${pos}"], select[data-index="option${pos}"]`);
      if (select && select.value !== val) {
        select.value = val;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this._syncBar();
  }

  /* ── Watch main picker changes ──────────────────────────── */

  _watchMainPicker() {
    if (!this.mainInput) return;

    const sync = () => {
      const id = parseInt(this.mainInput.value, 10);
      const opts = this._optionsFromId(id);
      // Only update if actually different (avoids re-entrancy from _pushToMainPicker)
      if (opts.some((o, i) => o !== this.selectedOptions[i])) {
        this.selectedOptions = opts;
        this._syncSwatches();
        this._syncBar();
      }
    };

    // Change events from the main picker
    this.mainInput.addEventListener('change', sync);
    document.getElementById(`MainProduct-${this.sectionId}`)
      ?.addEventListener('change', sync);

    // Custom variant events dispatched by various theme JS files
    document.addEventListener('variant:change', e => {
      if (!e.detail?.sectionId || e.detail.sectionId === this.sectionId) sync();
    });

    // Price element MutationObserver, catches theme JS re-rendering prices
    const priceWrapper = document.getElementById(`price-${this.sectionId}`);
    if (priceWrapper) {
      new MutationObserver(() => this._syncBar()).observe(priceWrapper, {
        subtree: true, childList: true, characterData: true,
      });
    }
  }

  /* ── Shake swatches that haven't been picked yet ─────────── */

  _shakeUnselected() {
    this.selectedOptions.forEach((val, i) => {
      if (val != null) return;
      this.bar.querySelectorAll(`.sticky-atc__swatch[data-option-position="${i + 1}"]`)
        .forEach(btn => {
          btn.classList.remove('needs-selection');
          // Force reflow so animation restarts if triggered twice
          void btn.offsetWidth;
          btn.classList.add('needs-selection');
          btn.addEventListener('animationend', () => btn.classList.remove('needs-selection'), { once: true });
        });
    });
  }

  /* ── Helpers ────────────────────────────────────────────── */

  _currentVariant() {
    if (!this.hasVariants) return this.variants[0] ?? null;
    return this.variants.find(v => {
      const opts = [v.option1, v.option2, v.option3];
      return this.selectedOptions.every((o, i) => o == null || opts[i] === o);
    }) ?? null;
  }

  _optionsFromId(id) {
    if (!id || isNaN(id)) return [null, null, null];
    const v = this.variants.find(v => v.id === id);
    return v ? [v.option1 ?? null, v.option2 ?? null, v.option3 ?? null] : [null, null, null];
  }
}

/* ── Bootstrap ──────────────────────────────────────────── */

(function () {
  function init() {
    document.querySelectorAll('[data-sticky-atc]').forEach(el => {
      new StickyATC(el.dataset.section);
    });
  }
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();
